import { computed, inject } from '@angular/core';
import {
  Agent,
  AgentCategory,
  AgentListResponse,
  AgentWritePayload,
  ModelCatalog,
  ToolCatalog,
} from '@interfaces/agent';
import { agentPermissionContext, canAgent } from '@interfaces/agent-access-control.types';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { categoryColor, filterAgents, groupAgentsByCategory } from '@pages/agents/agents.util';
import { AgentsService } from '@services/agents.service';
import { firstValueFrom } from 'rxjs';

type AgentsState = {
  agents: Agent[];
  categories: AgentCategory[];
  modelCatalog: ModelCatalog | null;
  toolCatalog: ToolCatalog | null;
  loading: boolean;
  error: string | null;
  selectedCategory: string | 'all';
  query: string;
  lastUpdated: number | null;
  readonly CACHE_DURATION_MS: number;
};

const initialState: AgentsState = {
  agents: [],
  categories: [],
  modelCatalog: null,
  toolCatalog: null,
  loading: false,
  error: null,
  selectedCategory: 'all',
  query: '',
  lastUpdated: null,
  CACHE_DURATION_MS: 5 * 60 * 1000,
};

function toArray(res: AgentListResponse | Agent[]): Agent[] {
  return Array.isArray(res) ? res : (res.results ?? []);
}

export const AgentsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  // Private agents are only visible to their owner — the whole library view is
  // driven off this filtered list, not the raw `agents` state.
  withComputed((store) => ({
    visibleAgents: computed(() => store.agents().filter((a) => canAgent('view', agentPermissionContext(a)))),
  })),
  withComputed((store) => ({
    agentCount: computed(() => store.visibleAgents().length),
    defaultCategoryUuid: computed(() => store.categories().find((c) => c.is_default)?.uuid ?? ''),
    filteredAgents: computed(() => filterAgents(store.visibleAgents(), store.selectedCategory(), store.query())),
    groups: computed(() =>
      groupAgentsByCategory(filterAgents(store.visibleAgents(), store.selectedCategory(), store.query())),
    ),
    noResults: computed(() => {
      const q = store.query().trim();
      return (
        q.length > 0 &&
        filterAgents(store.visibleAgents(), store.selectedCategory(), q).length === 0 &&
        store.visibleAgents().length > 0
      );
    }),
    categoryChips: computed(() => {
      const seen = new Map<string, { label: string; count: number }>();
      for (const a of store.visibleAgents()) {
        if (!a.category) continue;
        const existing = seen.get(a.category.uuid);
        if (existing) existing.count++;
        else seen.set(a.category.uuid, { label: a.category.name, count: 1 });
      }
      const entries = [...seen.entries()].map(([uuid, { label, count }]) => ({
        uuid,
        label,
        count,
        color: categoryColor(uuid),
      }));
      entries.sort((x, y) => (x.label === 'General' ? -1 : y.label === 'General' ? 1 : 0));
      return [{ uuid: 'all', label: 'All', count: store.visibleAgents().length, color: '' }, ...entries];
    }),
  })),
  withMethods((store, agentsService = inject(AgentsService)) => ({
    loadAgents(force = false): void {
      const fresh = store.lastUpdated() !== null && Date.now() - store.lastUpdated()! < store.CACHE_DURATION_MS();
      if (!force && fresh && store.agents().length > 0) return;
      patchState(store, { loading: true, error: null });
      agentsService.getAgents().subscribe({
        next: (res) => patchState(store, { agents: toArray(res), lastUpdated: Date.now() }),
        error: () => patchState(store, { error: 'agents_load_failed', loading: false }),
        complete: () => patchState(store, { loading: false }),
      });
    },
    loadCatalogs(): void {
      if (!store.modelCatalog()) {
        agentsService.getModelCatalog().subscribe({ next: (c) => patchState(store, { modelCatalog: c }) });
      }
      if (!store.toolCatalog()) {
        agentsService.getToolCatalog().subscribe({ next: (c) => patchState(store, { toolCatalog: c }) });
      }
    },
    setCategory(uuid: string | 'all'): void {
      patchState(store, { selectedCategory: uuid });
    },
    setQuery(q: string): void {
      patchState(store, { query: q });
    },
    async duplicate(uuid: string): Promise<Agent | null> {
      try {
        const copy = await firstValueFrom(agentsService.duplicateAgent(uuid));
        patchState(store, { agents: [copy, ...store.agents()] });
        return copy;
      } catch {
        patchState(store, { error: 'agent_duplicate_failed' });
        return null;
      }
    },
    async remove(uuid: string): Promise<boolean> {
      try {
        await firstValueFrom(agentsService.deleteAgent(uuid));
        patchState(store, { agents: store.agents().filter((a) => a.uuid !== uuid) });
        return true;
      } catch {
        patchState(store, { error: 'agent_delete_failed' });
        return false;
      }
    },
    loadCategories(): void {
      agentsService.getCategories().subscribe({
        next: (res) => patchState(store, { categories: Array.isArray(res) ? res : (res.results ?? []) }),
      });
    },
    async save(uuid: string | null, body: AgentWritePayload): Promise<Agent | null> {
      try {
        const saved = uuid
          ? await firstValueFrom(agentsService.updateAgent(uuid, body))
          : await firstValueFrom(agentsService.createAgent(body));
        const rest = store.agents().filter((a) => a.uuid !== saved.uuid);
        patchState(store, { agents: [saved, ...rest] });
        return saved;
      } catch {
        patchState(store, { error: 'agent_save_failed' });
        return null;
      }
    },
    async uploadAvatar(uuid: string, image: Blob): Promise<Agent | null> {
      try {
        const updated = await firstValueFrom(agentsService.uploadAvatar(uuid, image));
        const rest = store.agents().filter((a) => a.uuid !== updated.uuid);
        patchState(store, { agents: [updated, ...rest] });
        return updated;
      } catch {
        patchState(store, { error: 'avatar_upload_failed' });
        return null;
      }
    },
    async addCategory(name: string): Promise<AgentCategory | null> {
      try {
        const c = await firstValueFrom(agentsService.createCategory(name));
        patchState(store, { categories: [...store.categories(), c] });
        return c;
      } catch {
        patchState(store, { error: 'category_save_failed' });
        return null;
      }
    },
    async renameCategory(uuid: string, name: string): Promise<boolean> {
      try {
        const c = await firstValueFrom(agentsService.updateCategory(uuid, name));
        patchState(store, { categories: store.categories().map((x) => (x.uuid === uuid ? c : x)) });
        return true;
      } catch {
        patchState(store, { error: 'category_save_failed' });
        return false;
      }
    },
    async removeCategory(uuid: string): Promise<boolean> {
      try {
        await firstValueFrom(agentsService.deleteCategory(uuid));
        patchState(store, { categories: store.categories().filter((x) => x.uuid !== uuid) });
        // Backend reassigns that category's agents to General — refresh the agents list.
        // The agents store uses the object-literal method pattern (unlike spaces.store's
        // closure `return {}` pattern where siblings are called by name), so a sibling
        // `this.loadAgents()` call is not reliable here — inline the reload instead.
        agentsService.getAgents().subscribe({
          next: (res) => patchState(store, { agents: toArray(res) }),
        });
        return true;
      } catch {
        patchState(store, { error: 'category_delete_failed' });
        return false;
      }
    },
    clearStore(): void {
      patchState(store, initialState);
    },
    async loadAgent(uuid: string): Promise<Agent | null> {
      try {
        return await firstValueFrom(agentsService.getAgent(uuid));
      } catch {
        return null;
      }
    },
  })),
);
